/** @format */

import { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import LabelInput from "../../../shared/components/labelInput";
import Tq4Mapper from "../mappers/tq4Mapper";
import QuoteFormWrapper from "../../../shared/components/quoteFormWrapper";
import FormNavigationButtons from "../../../shared/components/FormNavigationButtons";
import QuoteStep from "../enums/QuoteStep";
import MonthYearField from "../../../shared/components/monthYearField";
import { handleNext } from "../../../shared/utils/navigationUtils";
import {
  fetchLicenceAuth,
  fetchNcdService,
} from "../../../shared/services/simpleListItemsService";
import { HotjarEventNames } from "../../../shared/utils/hotjarEventNames";
import { TrackHotjarEvent } from "../../../shared/utils/trackHotjarEvent";
import useAppStateStore from "../../../shared/store/AppStateStore";
import RadioGroup from "../../../shared/components/RadioGroup";

const Tq4 = () => {
  // Store
  // Form Setup
  const {
    register,
    formState: { errors, isValid },
    watch,
    control,
    setValue,
    trigger,
    handleSubmit,
  } = useForm({
    defaultValues: Tq4Mapper.get(),
  });

  // State
  const [lic, setlic] = useState([]);
  const [bonus, setBonus] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  const convertArrayFormat = (array) => {
    if (!Array.isArray(array)) {
      return [];
    }
    return array.map((item) => ({
      value: item.listItemId,
      label: item.listItemDescription,
    }));
  };

  // Effects
  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      try {
        const [licData, bonusData] = await Promise.all([
          fetchLicenceAuth(),
          fetchNcdService(),
        ]);

        setlic(licData);
        setBonus(bonusData);
      } catch (error) {
        console.error("Error fetching data:", error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, []);

  let datePassed = watch("datePassed");

  useEffect(() => {
    if (datePassed) trigger("datePassed");
  }, [datePassed]);

  useEffect(() => {
    TrackHotjarEvent(HotjarEventNames.TAXI_QUOTE_TQ4_ENTERED);
  }, []);

  // Event Handlers
  const onSubmit = (data) => {
    Tq4Mapper.set(data);
    if (isValid) {
      TrackHotjarEvent(HotjarEventNames.TAXI_QUOTE_TQ4_NEXT_CLICKED);
    }
    handleNext(QuoteStep.TQ5);
  };

  return (
    <>
      <form onSubmit={handleSubmit(onSubmit)}>
        <QuoteFormWrapper>
          <h1 className="margin-bottom-10">Driving History</h1>
          <p className="margin-bottom-30">
            Please tell us about your driving history.
          </p>

          <div className="fields">
            <LabelInput
              id="bonus"
              label="How many years of taxi no claim bonus?"
              type="select"
              options={convertArrayFormat(bonus)}
              control={control}
              placeholder="Select no claim bonus"
              register={register("bonus", { required: true })}
              errors={errors.bonus}
              watch={watch("bonus")}
              trigger={trigger}
              isSearchable={true}
              isLoading={isLoading}
            />
            {watch("bonus")?.value > 3 && (
              <RadioGroup
                name="NoClaimBonus"
                mainLabel="Would you like to protect your no claim bonus?"
                register={register}
                errors={errors.NoClaimBonus}
              />
            )}

            <LabelInput
              id="LicenseAuth"
              label="Licensing authority"
              type="select"
              options={convertArrayFormat(lic)}
              control={control}
              placeholder="Select licence authority"
              register={register("LicenseAuth", { required: true })}
              watch={watch("LicenseAuth")}
              trigger={trigger}
              isSearchable={true}
              isLoading={isLoading}
              errors={errors.LicenseAuth}
            />

            <MonthYearField
              label="When did you obtain your Private Hire License or Taxi Badge?"
              control={control}
              monthID="datePassedMonth"
              yearID="datePassedYear"
              errors={errors.datePassedMonth || errors.datePassedYear}
              trigger={trigger}
              watch={watch}
              register={register}
              setValue={setValue}
            />

            <RadioGroup
              name="vehicleUse"
              mainLabel="In addition to working Private Hire for Uber, do you work Public Hire or as a Hackney Carriage?"
              register={register}
              errors={errors.vehicleUse}
              yesValue={17}
              noValue={15}
            />

            <RadioGroup
              name="insuranceDeclined"
              mainLabel="Have you ever had insurance declined, cancelled or special terms imposed?"
              register={register}
              errors={errors.insuranceDeclined}
            />

            <RadioGroup
              name="convictedCharged"
              mainLabel="Have you ever been convicted, charged, or received a police
              caution for any crime (excluding traffic offenses)?"
              secondaryLabel={
                <>
                  If a conviction is now considered "spent" under the{" "}
                  <a
                    className="LinkStyle"
                    href="https://www.gov.uk/government/publications/new-guidance-on-the-rehabilitation-of-offenders-act-1974/guidance-on-the-rehabilitation-of-offenders-act-1974-and-the-exceptions-order-1975"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {" "}
                    Rehabilitation of Offenders Act 1974
                  </a>
                  , please note that it does not need to be disclosed.
                </>
              }
              register={register}
              errors={errors.convictedCharged}
            />

            <RadioGroup
              name="addAdditionalDriver"
              mainLabel="Are you the only driver insured on the vehicle?"
              register={register}
              errors={errors.addAdditionalDriver}
              labelYes="Yes"
              labelNo="No"
              yesValue="No"
              noValue="Yes"
            />
            {watch("addAdditionalDriver") === "Yes" ? (
              <p className="licPara-noMargin">
                You can only add drivers who hold a valid Taxi badge
              </p>
            ) : null}
          </div>
        </QuoteFormWrapper>
        <FormNavigationButtons prevStep={QuoteStep.TQ3} isValid={isValid} />
      </form>
    </>
  );
};

export default Tq4;
